import React, { forwardRef, useEffect } from "react";
import {
  useDataBoundsForm,
  DATA_BOUNDS_FIELDS,
} from "./useDataBoundsForm";
import styles from "../../../styles/patient-page-modules/OrgDataBounds.module.css";

/**
 * Renders the LOW / HIGH data-bounds table matching the Figma design.
 * Reuses the same react-hook-form + zod logic as the patient-settings
 * DataBounds component (via useDataBoundsForm), but with its own markup
 * + CSS module so it can match the org-level modal design exactly.
 *
 * ref.current.getValidatedData() -> Promise<convertedValues | null>
 * ref.current.isDirty -> boolean (whether form has unsaved changes)
 */
const DataBoundsFigmaFields = forwardRef(({ data_bounds, onFormChange }, ref) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useDataBoundsForm(data_bounds, ref);

  // Watch all form values and notify parent of changes
  const formValues = watch();
  useEffect(() => {
    if (ref.current && onFormChange) {
      onFormChange(ref.current.isDirty);
    }
  }, [formValues, onFormChange]);

  return (
    <div>
      <p className={styles.description}>
        Set the low and high bounds for each vital. Readings outside these ranges flag all the patient under this organization. Defaults follow standard clinical ranges.
      </p>

      <div className={styles.table}>
        <div />
        <p className={styles.headerLabel}>Low</p>
        <p className={styles.headerLabel}>High</p>

        {DATA_BOUNDS_FIELDS.map(({ key, label, unit }) => (
          <React.Fragment key={key}>
            <div className={styles.fieldLabelCell}>
              <p className={styles.fieldName}>{label}</p>
              <p className={styles.fieldUnit}>{unit}</p>
            </div>

            <div className={styles.inputWrapper}>
              <input
                type="number"
                step="any"
                {...register(`${key}_low`)}
                className={`${styles.input} ${
                  errors[`${key}_low`] ? styles.inputError : ""
                }`}
              />
              {errors[`${key}_low`] && (
                <p className={styles.errorMessage}>
                  {errors[`${key}_low`].message}
                </p>
              )}
            </div>

            <div className={styles.inputWrapper}>
              <input
                type="number"
                step="any"
                {...register(`${key}_high`)}
                className={`${styles.input} ${
                  errors[`${key}_high`] ? styles.inputError : ""
                }`}
              />
              {errors[`${key}_high`] && (
                <p className={styles.errorMessage}>
                  {errors[`${key}_high`].message}
                </p>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
});

export default DataBoundsFigmaFields;